import { useEffect, useState } from "react";

import { UpdateField, useFormState } from "common/hooks";
import { OrderStatus as OrderStatusEnum } from "common/types/orders";
import { toDollars } from "common/utils/formatting";
import { pluralized } from "common/utils/strings";
import LinkButton from "common/components/LinkButton";
import errorImages from "common/constants/errorImages";
import CatalogImage from "common/components/CatalogImage";
import { Optional } from "common/utils/types";

import Modal from "client/common/components/deprecated-modals/Modal";
import TextInput from "client/common/components/inputs/TextInput";
import Box from "client/common/components/layouts/Box";
import { useOrderLookupQuery } from "client/orders/hooks";
import { IOrderItem, OrderLookupSearch, IOrderItemCancelationNote } from "client/orders/types";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import OrderStatus from "client/orders/components/OrderStatus";
import PillButton from "client/common/components/buttons/PillButton";
import Status from "client/common/components/content/Status";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import { urls } from "client/routes";
import { sendSelectItem } from "client/analytics/utils";
import CrossedPrice from "client/common/components/content/CrossedPrice";

import styles from "../styles/SupportOrderStatusModal.module.less";

enum SupportOrderStatusStepEnum {
  Form = "form",
  Detail = "detail",
}

type SupportOrderStatusFormStepProps = {
  values: OrderLookupSearch;
  onUpdateField: UpdateField<OrderLookupSearch>;
  onNext: () => void;
  onClose: () => void;
};

const SupportOrderStatusFormStep = ({
  values,
  onUpdateField,
  onNext,
  onClose,
}: SupportOrderStatusFormStepProps) => {
  const canSubmit = Object.values(values).every((v) => !!v);
  return (
    <ModalLayout>
      <ModalLayout.Header
        title="Check order status"
        onClose={onClose}
        description={
          <>
            You can check the current status of your order{" "}
            <LinkButton className={styles.link} dest={urls.orders.list()}>
              on your orders page
            </LinkButton>{" "}
            or enter your order number below.
          </>
        }
      />
      <ModalLayout.Form canSubmit={canSubmit} onSubmit={onNext} submitText="Find Order">
        <TextInput
          value={values.display_number}
          label="Order Number"
          onChange={(num) => onUpdateField("display_number")(num.trim())}
        />
      </ModalLayout.Form>
    </ModalLayout>
  );
};

type SupportOrderStatusItemProps = {
  orderItem: IOrderItem;
};

const SupportOrderStatusItem = ({ orderItem }: SupportOrderStatusItemProps) => {
  const itemDest = urls.products.detail({
    params: { id: orderItem.item.product.product_line.id },
    search: {
      item: orderItem.item.id,
      product: orderItem.item.product.id,
    },
  });

  const getCancelationNoteIntro = (note: IOrderItemCancelationNote) => {
    if (note.author) {
      return `Cancelation note from ${note.author}:`;
    }
    return "Cancelation note:";
  };

  return (
    <div className={styles.item}>
      <CatalogImage.Link
        dest={itemDest}
        image={orderItem.item.image}
        size="thumbnail"
        alt={orderItem.item.full_name}
        className={styles.image}
        onClick={() => sendSelectItem(orderItem.item, "Order Support", orderItem.unit_price)}
      />
      <div>
        <LinkButton
          dest={itemDest}
          className={styles.titleContainer}
          onClick={() => sendSelectItem(orderItem.item, "Order Support", orderItem.unit_price)}
        >
          <p className={styles.title}>{orderItem.item.product.product_line.name}</p>
          {orderItem.is_canceled && <Status color="danger">Canceled</Status>}
        </LinkButton>
        <p className={styles.description}>{orderItem.item.product_line_relative_name}</p>
        {orderItem.patient && (
          <p className={styles.description}>For {orderItem.patient.name_display}</p>
        )}
        <div className={styles.data}>
          <div className={styles.quantity}>x {orderItem.quantity}</div>
          <CrossedPrice
            classNames={{ price: styles.mainPrice, crossed: styles.crossedPrice }}
            crossed={orderItem.subtotal}
            price={orderItem.subtotal - orderItem.amortized_discount}
          />
        </div>
        {!!orderItem.cancelation_note && (
          <p className={styles.cancelationNote}>
            <span className={styles.boldNote}>
              {getCancelationNoteIntro(orderItem.cancelation_note)}
            </span>{" "}
            {orderItem.cancelation_note.note}
          </p>
        )}
      </div>
    </div>
  );
};

type SupportOrderStatusDetailStepProps = {
  values: OrderLookupSearch;
  onPrevious: () => void;
  onClose: () => void;
};

const SupportOrderStatusDetailStep = ({
  values,
  onPrevious,
  onClose,
}: SupportOrderStatusDetailStepProps) => {
  const { data: order, isLoading, error } = useOrderLookupQuery(values);

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (!order || error) {
    return (
      <ModalLayout>
        <ModalLayout.Header
          title="Check order status"
          onClose={onClose}
          description={
            <>
              You can check the current status of your order{" "}
              <LinkButton className={styles.link} dest={urls.orders.list()}>
                via your orders
              </LinkButton>{" "}
              or{" "}
              <button type="button" className={styles.link} onClick={onPrevious}>
                try another order number
              </button>
              .
            </>
          }
        />
        <ModalLayout.Content>
          <FetchErrorContainer image={errorImages.EmptyOrders} error={error} entityName="order" />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button type="button" onClick={onPrevious}>
            Try Another Number
          </ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    );
  }

  const summaryItems = [
    {
      label: "Name",
      value: order.client_name,
    },
    {
      label: "Total",
      value: `${toDollars(order.total)} (${order.num_items} ${pluralized(
        order.num_items,
        "item",
      )})`,
    },
  ];

  return (
    <ModalLayout>
      <ModalLayout.Header
        title="Check order status"
        onClose={onClose}
        description={
          <div>
            You can see all of your orders{" "}
            <LinkButton className={styles.link} dest={urls.orders.list()}>
              on your orders page
            </LinkButton>{" "}
            or{" "}
            <button type="button" className={styles.link} onClick={onPrevious}>
              check another order
            </button>
            .
          </div>
        }
      />
      <Box className={styles.detail}>
        <div className={styles.header}>
          <div className={styles.titles}>
            <h4 className={styles.title}>Order {order.display_number}</h4>
            <div className={styles.status}>
              <OrderStatus order={order} />
            </div>
          </div>
          <div className={styles.summary}>
            {summaryItems.map((summaryItem) => (
              <div className={styles.summaryItem} key={summaryItem.label}>
                <div className={styles.value}>{summaryItem.value}</div>
                <div className={styles.label}>{summaryItem.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.detailContent}>
          <div className={styles.contentItems}>
            {order.ungrouped_items.map((orderItem) => (
              <div key={orderItem.id} className={styles.group}>
                <div className={styles.itemGroup}>
                  <SupportOrderStatusItem orderItem={orderItem} />
                </div>
              </div>
            ))}
            {order.fulfillments.map((fulfillment) => (
              <div key={fulfillment.id} className={styles.group}>
                <div className={styles.itemGroup}>
                  {fulfillment.items.map((orderItem) => (
                    <SupportOrderStatusItem key={orderItem.id} orderItem={orderItem} />
                  ))}
                </div>
              </div>
            ))}
            {order.trackers.map((tracker) => (
              <div key={tracker.id} className={styles.group}>
                <div className={styles.trackerGroup}>
                  <div className={styles.trackerItems}>
                    {tracker.items.map((orderItem) => (
                      <SupportOrderStatusItem key={orderItem.id} orderItem={orderItem} />
                    ))}
                  </div>
                  <div className={styles.trackerInfo}>
                    <p className={styles.status}>{tracker.status_display}</p>
                    {tracker.last_updated && (
                      <div className={styles.updatedContainer}>
                        <p className={styles.updated}>Last updated</p>
                        <p className={styles.updated}>{tracker.last_updated}</p>
                      </div>
                    )}
                    {!!tracker?.tracking_url && (
                      <PillButton size="xs" variant="secondary" href={tracker.tracking_url}>
                        Track
                      </PillButton>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.orderInfo}>
            {order.status === OrderStatusEnum.ApprovalNeeded && (
              <div className={styles.orderStatusMessage}>
                This order is in progress and currently{" "}
                <span className={styles.boldMessage}>awaiting veterinarian approval</span>. Once it
                is approved, you can track shipping updates here or{" "}
                <LinkButton className={styles.link} dest={urls.orders.list()}>
                  via your orders
                </LinkButton>{" "}
                on your account.
              </div>
            )}
            <PillButton
              size="s"
              dest={urls.orders.detail({ params: { id: order.id } })}
              variant="secondary"
            >
              View Order Details
            </PillButton>
          </div>
        </div>
      </Box>
    </ModalLayout>
  );
};

const exhaustiveCheck = (step: never) => {
  console.warn(`Illegal support order status step ${step}`);
  return null;
};

type SupportOrderStatusStepProps = {
  step: SupportOrderStatusStepEnum;
  values: OrderLookupSearch;
  onUpdateField: UpdateField<OrderLookupSearch>;
  onSetStep: (newStep: SupportOrderStatusStepEnum) => void;
  onClose: () => void;
};

const SupportOrderStatusStep = ({
  step,
  onUpdateField,
  onSetStep,
  ...rest
}: SupportOrderStatusStepProps) => {
  switch (step) {
    case SupportOrderStatusStepEnum.Form:
      return (
        <SupportOrderStatusFormStep
          onUpdateField={onUpdateField}
          onNext={() => onSetStep(SupportOrderStatusStepEnum.Detail)}
          {...rest}
        />
      );
    case SupportOrderStatusStepEnum.Detail:
      return (
        <SupportOrderStatusDetailStep
          onPrevious={() => onSetStep(SupportOrderStatusStepEnum.Form)}
          {...rest}
        />
      );
    default:
      return exhaustiveCheck(step);
  }
};

type SupportOrderStatusModalProps = {
  visible: boolean;
  onClose: () => void;
  initialValues?: Optional<OrderLookupSearch>;
};

const SupportOrderStatusModal = ({
  visible,
  onClose,
  initialValues: initialValuesProp,
}: SupportOrderStatusModalProps) => {
  const initialValues = initialValuesProp ?? { display_number: "" };
  const [step, setStep] = useState(SupportOrderStatusStepEnum.Form);
  const [values, updateField, setValues] = useFormState<OrderLookupSearch>(initialValues);

  useEffect(() => {
    if (visible) {
      setStep(
        initialValues.display_number
          ? SupportOrderStatusStepEnum.Detail
          : SupportOrderStatusStepEnum.Form,
      );
      setValues(initialValues);
    }
    // we only care about defaulting if the visibility of the modal changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <Modal visible={visible} onClose={onClose}>
      <SupportOrderStatusStep
        step={step}
        values={values}
        onUpdateField={updateField}
        onSetStep={setStep}
        onClose={onClose}
      />
    </Modal>
  );
};

export default SupportOrderStatusModal;
